<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div ref="parent" v-on-clickaway="hideContactList" class="relative">
    <woot-phone-input
      v-if="isPhoneType"
      ref="input"
      :value="phoneNumber"
      :placeholder="
        placeholder || $t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')
      "
      :error="error"
      :show-flag-in-selection="false"
      :only-countries="[]"
      :enable-search-country="true"
      :fixed-dropdown="true"
      class="mb-0"
      @input="inputValue = $event"
      @focus="showContactList"
      @dropdown-toggle="onCountryDropdownToggle"
      @blur="$emit('blur')"
    />
    <input
      v-else
      ref="input"
      v-model="inputValue"
      type="text"
      :placeholder="
        placeholder || $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ENTER_EMAIL')
      "
      class="mb-0"
      @focus="showContactList"
      @blur="$emit('blur')"
    />
    <woot-dropdown-menu
      v-if="searchContacts.length && showContacts"
      class="bg-white rounded-b border border-solid border-slate-200 dark:border-slate-700 max-h-72 overflow-auto divide-y divide-slate-200"
      :style="dropdownStyle"
    >
      <!-- Do not replace v-show with computed property, clickaway won't work properly  -->
      <woot-dropdown-item
        v-for="contact in searchContacts"
        v-show="!alreadySelected.includes(contact.email)"
        :key="contact.id"
        class="!mb-0"
      >
        <woot-button
          variant="hollow"
          color-scheme="secondary"
          class="w-full px-2.5 border-none rounded-none h-16 truncate"
          @click="onSelected(contact)"
        >
          <contact-dropdown-item
            :thumbnail="contact.thumbnail"
            :identifier="contact.id"
            :name="contact.name"
            :phone-number="contact.phone_number"
            :email="contact.email"
            :company-name="contact.additional_attributes.company_name"
          />
        </woot-button>
      </woot-dropdown-item>
    </woot-dropdown-menu>
  </div>
</template>

<script>
import ContactAPI from 'dashboard/api/contacts';
import { mixin as clickaway } from 'vue-clickaway';
import { getFixedDialogStyle } from 'dashboard/helper/dialogPositionHelper';
import rtlMixin from 'shared/mixins/rtlMixin';
import ContactDropdownItem from './ContactDropdownItem.vue';

export default {
  components: { ContactDropdownItem },
  mixins: [clickaway, rtlMixin],
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    phoneNumber: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    field: {
      type: String,
      default: 'phone_number',
    },
    skipSelected: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isRequested: false,
      showContacts: false,
      searchContacts: [],
      dropdownStyle: {},
    };
  },
  computed: {
    inputValue: {
      get() {
        if (this.field == 'phone_number') return this.phoneNumber;
        return this.email;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    alreadySelected() {
      return this.email
        .replaceAll(',', ' ')
        .split(' ')
        .filter(val => val);
    },
    isPhoneType() {
      return this.field === 'phone_number';
    },
  },
  watch: {
    phoneNumber(newVal) {
      this.getContacts(newVal);
    },
    email(newVal) {
      this.getContacts(this.emailQuery(newVal));
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScrollRecalculate, true);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScrollRecalculate, true);
  },
  methods: {
    // The field sits inside the modal's scrolling body, so an absolutely
    // positioned dropdown would extend that scroll area instead of overlaying
    // it. Pin it to the viewport against the field's rect instead.
    calculateDropdownPosition() {
      const el = this.$refs.parent;
      if (!el) return;
      const width = el.offsetWidth;
      this.dropdownStyle = {
        ...getFixedDialogStyle(el, {
          width,
          height: 288,
          gap: 4,
          align: this.isRTLView ? 'right' : 'left',
        }),
        width: `${width}px`,
        zIndex: 9999,
      };
    },
    onScrollRecalculate() {
      if (this.showContacts) this.calculateDropdownPosition();
    },
    // The country trigger sits inside this component, so clickaway never
    // fires for it — close the contact list explicitly instead.
    onCountryDropdownToggle(isOpen) {
      if (isOpen) this.hideContactList();
    },
    emailQuery(newVal) {
      return this.multiple ? newVal.split(',').slice(-1)[0] : newVal;
    },
    async getContacts(query) {
      this.isRequested = true;
      try {
        const {
          data: { payload },
        } = await ContactAPI.search(query, 1, 'name', '', this.field);
        this.searchContacts = payload;
      } catch (error) {
        console.log(error);
        this.showAlert(this.$t('MERGE_CONTACTS.SEARCH.ERROR_MESSAGE'));
      }
    },
    onSelected(contact) {
      this.$emit('on-select-contact', contact);
      if (this.multiple) {
        this.$refs.input.focus();
        return;
      }
      this.showContacts = false;
    },
    showContactList() {
      this.calculateDropdownPosition();
      this.showContacts = true;
      this.getContacts(this.phoneNumber || this.emailQuery(this.email));
    },
    hideContactList() {
      this.showContacts = false;
    },
  },
};
</script>
<style lang="scss" scoped></style>
